import axios from 'axios';
// config
import { HOST_API } from 'src/config-global';
// cache
import cacheManager, { shouldCache, getInvalidationPatterns, extractResourceInfo } from 'src/utils/cache';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    login: '/v1/auth/login',
  },
  clinic: {
    get: '/v1/clinic',
    create: '/v1/clinic',
  },
};

// Helper function to get cached data for GET requests
const getCachedData = (methodUpper, cacheUrl) => {
  if (methodUpper === 'GET' && shouldCache(methodUpper, cacheUrl)) {

    const cacheKey = cacheManager.generateCacheKey(cacheUrl);
    const cached = cacheManager.get(cacheKey);
    if (cached !== null) {
      return cached;
    }
  }
  return null;
};

// Helper function to cache response data for GET requests
const cacheResponseData = (methodUpper, cacheUrl, responseData) => {
  if (methodUpper === 'GET' && shouldCache(methodUpper, cacheUrl)) {
    const cacheKey = cacheManager.generateCacheKey(cacheUrl);
    cacheManager.set(cacheKey, responseData);
  }
};

// Helper function to invalidate cache on mutations
const invalidateCacheOnMutation = (methodUpper, cacheUrl) => {
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(methodUpper)) {
    const patterns = getInvalidationPatterns(methodUpper, cacheUrl);
    patterns.forEach((pattern) => {
      cacheManager.invalidate(pattern);
    });

    // Also invalidate by resource type
    const { resourceType, resourceId } = extractResourceInfo(cacheUrl);
    if (resourceType) {
      cacheManager.invalidateResource(resourceType, resourceId);
    }
  }
};

export const requestWithToken = async (method, url, data = {}, token = '') => {
  const methodUpper = method.toUpperCase();
  const fullUrl = url.startsWith('http') ? url : `${HOST_API}${url}`;
  const cacheUrl = fullUrl;

  try {
    // Check cache for GET requests
    const cached = getCachedData(methodUpper, cacheUrl);
    if (cached !== null) {
      return cached;
    }

    const response = await axios({
      method,
      url: fullUrl,
      data,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    // Cache GET responses or invalidate on mutations
    cacheResponseData(methodUpper, cacheUrl, response.data);
    invalidateCacheOnMutation(methodUpper, cacheUrl);

    return response.data;
  } catch (error) {
    // If the server responds with an error (e.g., 4xx or 5xx status codes), throw it so that it can be caught in the .catch block
    if (error.response) {
      throw new Error(error.response.data.message || 'Request failed');
    }
    // Handle other types of errors (e.g., network errors)
    throw new Error(error.message || 'An unknown error occurred');
  }
};

export const requestWithoutToken = async (method, url, data = {}) => {
  const methodUpper = method.toUpperCase();
  const fullUrl = url.startsWith('http') ? url : `${HOST_API}${url}`;
  const cacheUrl = fullUrl;

  try {
    // Check cache for GET requests
    const cached = getCachedData(methodUpper, cacheUrl);
    if (cached !== null) {
      return cached;
    }

    const response = await axios({
      method,
      url: fullUrl,
      data,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Cache GET responses or invalidate on mutations
    cacheResponseData(methodUpper, cacheUrl, response.data);
    invalidateCacheOnMutation(methodUpper, cacheUrl);

    return response.data;
  } catch (error) {
    // If the server responds with an error (e.g., 4xx or 5xx status codes), throw it so that it can be caught in the .catch block
    if (error.response) {
      throw new Error(error.response.data.message || 'Request failed');
    }
    // Handle other types of errors (e.g., network errors)
    throw new Error(error.message || 'An unknown error occurred');
  }
};

export const getData = async (url, token = getToken(), data = null) =>
  requestWithToken('get', url, data, token);

export const postData = async (url, data = {}, token = getToken()) =>
  requestWithToken('post', url, data, token);


export const postDataGuest = async (url, data = {}) => requestWithoutToken('post', url, data);

export const getDataGuest = async (url) => requestWithoutToken('get', url);

export const putDataGuest = async (url) => requestWithoutToken('put', url);

export const putData = async (url, data = {}, token = getToken()) =>
  requestWithToken('put', url, data, token);

export const patchData = async (url, data = {}, token = getToken()) =>
  requestWithToken('patch', url, data, token);

export const deleteData = async (url, token = getToken()) => requestWithToken('delete', url, {}, token);

export const postFormData = async (url, formData, token = getToken()) => {
  const fullUrl = url.startsWith('http') ? url : `${HOST_API}${url}`;
  const cacheUrl = fullUrl;

  try {
    const response = await axios({
      method: 'post',
      url: fullUrl,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data', // Use multipart/form-data for form data
        Authorization: `Bearer ${token}`,
      },
    });

    // Invalidate cache on mutations (POST request)
    invalidateCacheOnMutation('POST', cacheUrl);

    return response.data;
  } catch (error) {
    // If the server responds with an error (e.g., 4xx or 5xx status codes), throw it so that it can be caught in the .catch block
    if (error.response) {
      throw new Error(error.response.data.message || 'Request failed');
    }
    // Handle other types of errors (e.g., network errors)
    throw new Error(error.message || 'An unknown error occurred');
  }
};

export const getToken = () => {
  const STORAGE_KEY = 'accessToken';
  return localStorage.getItem(STORAGE_KEY);
};
